import { action } from '@/lib/api';
import clone from '@/lib/clone';
import mergeByProp from '@/lib/mergeByProp';
import pushChangesQueueItem from '@/redux/actions/pushChangesQueueItem.js';
import { store } from '@/redux/lib/configureStore';
import { getDeep } from '@shared/utils/getDeep.js';
import { setDeep } from '@shared/utils/setDeep.js';
import appScreenSelectors from '../selectors/appScreenSelectors';

const appActions = {
  set(path, payload) {
    return new Promise((resolve) => {
      const state = store.getState();
      const activeApp = clone(appScreenSelectors.activeApp(state));
      const newApp = setDeep(activeApp, path, payload);
      const value = getDeep(path, newApp);
      pushChangesQueueItem('app', { appId: activeApp?.id, appVersion: activeApp?.version, path, value }, newApp);
      resolve(newApp);
    });
  },

  setActionHeaders({ appId, appVersion, actionId, headers }) {
    const state = store.getState();
    const app = state?.workflow?.appsVersions?.find((candidate) => candidate?.id === appId && String(candidate?.version) === String(appVersion));
    const actionIndex = app?.actions?.findIndex((candidate) => candidate?.id === actionId) ?? -1;

    if (!app || actionIndex === -1) {
      throw new Error(`Could not update action headers: expected app ${appId}@${appVersion} with action ${actionId}.`);
    }

    const actions = [...app.actions];
    actions[actionIndex] = {
      ...actions[actionIndex],
      headers
    };
    const newApp = {
      ...app,
      actions
    };
    const path = `actions[id:${actionId}].headers`;

    pushChangesQueueItem('app', { appId, appVersion: app.version, path, value: headers }, newApp);
    return Promise.resolve(newApp);
  },

  merge(path, prop, payload) {
    payload = Array.isArray(payload) ? payload : [payload];
    return new Promise((resolve) => {
      const state = store.getState();
      const activeApp = clone(appScreenSelectors.activeApp(state));
      const newApp = setDeep(activeApp, path, (deepValue) => mergeByProp(prop, deepValue || [], payload));
      const value = getDeep(path, newApp);
      pushChangesQueueItem('app', { appId: activeApp?.id, appVersion: activeApp?.version, path, value }, newApp);
      resolve();
    });
  },

  filter(path, fn) {
    return new Promise((resolve) => {
      const state = store.getState();
      const activeApp = clone(appScreenSelectors.activeApp(state));
      const newApp = setDeep(activeApp, path, (deepValue) => deepValue.filter(fn));
      const value = getDeep(path, newApp);
      pushChangesQueueItem('app', { appId: activeApp?.id, appVersion: activeApp?.version, path, value }, newApp);
      resolve();
    });
  },

  append(path, payload) {
    return new Promise((resolve) => {
      const state = store.getState();
      const activeApp = clone(appScreenSelectors.activeApp(state));

      let newApp;
      let value;

      const id = payload?.id;
      if (id) {
        path = `${path}[id:${id}]`;
      }

      if (id) {
        newApp = setDeep(activeApp, path, payload);
        value = getDeep(path, newApp);
      } else {
        newApp = setDeep(activeApp, path, (deepValue) => (Array.isArray(deepValue) ? [...(deepValue || []), payload] : deepValue || []));
        value = getDeep(path, newApp);
      }

      pushChangesQueueItem('app', { appId: activeApp?.id, appVersion: activeApp?.version, path, value }, newApp);
      resolve();
    });
  },

  deleteApp(id) {
    return new Promise((resolve) => {
      action('DELETE_APP', { id });
      pushChangesQueueItem('app', { operation: 'delete', appId: id });
      resolve();
    });
  }
};

export default appActions;
